/**
 * Convert a Zod schema to the JSON Schema dialect Anthropic structured
 * outputs accept.
 *
 * z.toJSONSchema alone is not enough: the API rejects schemas containing
 * numeric/string/array size constraints (minimum, maxLength, minItems, ...)
 * and requires additionalProperties: false on every object — a schema-valued
 * additionalProperties (from z.record()) is rejected too, so record fields
 * can only ever come back empty. The stripped constraints are not lost:
 * the same Zod schema validates the response client-side at parse time.
 */

import { z } from 'zod';

const UNSUPPORTED_CONSTRAINT_KEYS = [
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'multipleOf',
  'minLength',
  'maxLength',
  'minItems',
  'maxItems',
  'default',
  'propertyNames',
] as const;

// Keys whose value is a map of property-name -> subschema. Constraint
// stripping must not touch the map keys themselves — a property named
// "minimum" is data, not a constraint.
const NESTED_SCHEMA_MAPS = ['properties', '$defs', 'definitions'] as const;
const NESTED_SCHEMA_LISTS = ['anyOf', 'allOf', 'oneOf', 'prefixItems'] as const;
const NESTED_SCHEMA_KEYS = ['items', 'not'] as const;

function sanitizeNode(node: unknown): void {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return;
  const schema = node as Record<string, unknown>;

  for (const key of UNSUPPORTED_CONSTRAINT_KEYS) {
    delete schema[key];
  }
  if (schema.type === 'object') {
    schema.additionalProperties = false;
  }

  for (const key of NESTED_SCHEMA_MAPS) {
    const map = schema[key];
    if (map && typeof map === 'object' && !Array.isArray(map)) {
      for (const child of Object.values(map)) sanitizeNode(child);
    }
  }
  for (const key of NESTED_SCHEMA_LISTS) {
    const list = schema[key];
    if (Array.isArray(list)) {
      for (const child of list) sanitizeNode(child);
    }
  }
  for (const key of NESTED_SCHEMA_KEYS) {
    sanitizeNode(schema[key]);
  }
}

/** Convert a Zod schema to an Anthropic-compatible JSON Schema. */
export function toStructuredOutputSchema(schema: z.ZodType): Record<string, unknown> {
  const json = z.toJSONSchema(schema) as Record<string, unknown>;
  delete json.$schema;
  sanitizeNode(json);
  return json;
}
